import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronsUpDown, Trash2, X } from 'lucide-react'

type MultiSelectOption = {
  label: string
  value: string
}

interface MultiSelectCheckboxProps {
  label?: string
  options: MultiSelectOption[]
  selected: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  allLabel?: string
  clearLabel?: string
  disabled?: boolean
  testId?: string
}

export default function MultiSelectCheckbox({
  label,
  options,
  selected,
  onChange,
  placeholder,
  allLabel,
  clearLabel,
  disabled = false,
  testId,
}: MultiSelectCheckboxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const optionMap = useMemo(() => {
    const map = new Map<string, MultiSelectOption>()
    options.forEach((option) => {
      map.set(option.value, option)
    })
    return map
  }, [options])

  const selectedOptions = useMemo(
    () =>
      selected
        .map((value) => optionMap.get(value))
        .filter((option): option is MultiSelectOption => Boolean(option)),
    [selected, optionMap],
  )

  useEffect(() => {
    console.debug('[MultiSelectCheckbox] seleção atualizada', {
      label,
      selectedCount: selectedOptions.length,
      selectedValues: selected,
    })
  }, [label, selected, selectedOptions.length])

  const areAllSelected =
    options.length > 0 && selectedOptions.length === options.length

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const toggleOption = (value: string) => {
    if (disabled) {
      return
    }

    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const handleSelectAll = () => {
    if (disabled || areAllSelected) {
      return
    }

    onChange(options.map((option) => option.value))
  }

  const handleClear = () => {
    if (disabled || selected.length === 0) {
      return
    }

    onChange([])
  }

  const handleRemovePill = (value: string) => {
    if (disabled) {
      return
    }

    onChange(selected.filter((item) => item !== value))
  }

  return (
    <div className="space-y-2">
      {label && (
        <span className="text-sm font-semibold text-brand-text-primary">{label}</span>
      )}

      <div ref={containerRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-label={label ? `Filtro ${label}` : 'Filtro multi-select'}
          data-testid={testId ? `${testId}-toggle` : undefined}
          className="flex w-full items-center justify-between rounded-lg border border-brand-neutral-300 bg-white px-2.5 py-2 text-left text-sm text-brand-text-primary shadow-sm transition focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:ring-offset-2 focus:ring-offset-brand-surface-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex-1 truncate">
            {selectedOptions.length === 0 ? (
              <span className="text-brand-text-muted">
                {placeholder ?? 'Selecionar...'}
              </span>
            ) : selectedOptions.length === options.length ? (
              'Todos selecionados'
            ) : (
              `${selectedOptions.length} selecionado${
                selectedOptions.length > 1 ? 's' : ''
              }`
            )}
          </span>
          <ChevronsUpDown
            className={`ml-2 h-4 w-4 text-brand-text-muted transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {isOpen && (
          <div className="absolute left-0 right-0 z-20 mt-2 rounded-lg border border-brand-neutral-300 bg-white shadow-xl">
            <div className="flex items-center justify-center gap-3 border-b border-brand-neutral-200 px-2 py-2 bg-brand-surface-50">
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={areAllSelected || disabled}
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-brand-text-primary transition hover:bg-brand-surface-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Check className="h-3.5 w-3.5" />
                {allLabel ?? 'Selecionar todos'}
              </button>
              <button
                type="button"
                onClick={handleClear}
                disabled={selected.length === 0 || disabled}
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-brand-text-primary transition hover:bg-brand-surface-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {clearLabel ?? 'Limpar'}
              </button>
            </div>

            <ul className="max-h-56 overflow-y-auto py-2 bg-white">
              {options.map((option) => {
                const isChecked = selected.includes(option.value)

                return (
                  <li key={option.value}>
                    <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm text-brand-text-primary transition hover:bg-brand-surface-100">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-brand-neutral-300 text-brand-primary-500 focus:ring-brand-primary-500"
                        checked={isChecked}
                        onChange={() => toggleOption(option.value)}
                        disabled={disabled}
                      />
                      <span>{option.label}</span>
                      {isChecked && (
                        <Check className="ml-auto h-4 w-4 text-brand-primary-500" />
                      )}
                    </label>
                  </li>
                )
              })}

              {options.length === 0 && (
                <li className="px-3 py-2 text-sm text-brand-text-muted">
                  Nenhuma opção disponível
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedOptions.map((option) => (
            <span
              key={option.value}
              className="inline-flex items-center gap-1 rounded-full bg-brand-primary-50 px-3 py-1 text-xs font-medium text-brand-primary-700 ring-1 ring-inset ring-brand-primary-200"
            >
              {option.label}
              <button
                type="button"
                onClick={() => handleRemovePill(option.value)}
                className="rounded-full p-0.5 text-brand-primary-600 transition hover:bg-brand-primary-100 focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed"
                disabled={disabled}
                aria-label={`Remover ${option.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}